import { useState, useEffect, useCallback } from 'react';
import { useAdmin } from '@/contexts/AdminContext';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import logger from '@/utils/logger';
import {
  Search, Filter, Download, RefreshCw, ChevronLeft, ChevronRight,
  MoreHorizontal, Pencil, Trash2, UserPlus, Check, X as XIcon, Eye
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STAGES = ['new', 'contacted', 'proposal_sent', 'negotiation', 'follow_up', 'won', 'lost', 'ghosted'];
const PRIORITIES = ['low', 'medium', 'high', 'urgent'];

const STAGE_COLORS = {
  new: 'bg-blue-100 text-blue-700',
  contacted: 'bg-amber-100 text-amber-700',
  proposal_sent: 'bg-purple-100 text-purple-700',
  negotiation: 'bg-pink-100 text-pink-700',
  follow_up: 'bg-cyan-100 text-cyan-700',
  won: 'bg-green-100 text-green-700',
  lost: 'bg-red-100 text-red-700',
  ghosted: 'bg-slate-100 text-slate-500',
};

const PRIORITY_BADGES = {
  urgent: 'bg-red-500 text-white',
  high: 'bg-orange-500 text-white',
  medium: 'bg-yellow-100 text-yellow-700',
  low: 'bg-green-100 text-green-700',
};

export default function MetaAdsLeads({ onViewLead }) {
  const { getAuthHeaders } = useAdmin();
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [stageCounts, setStageCounts] = useState({});
  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [forms, setForms] = useState([]);
  const [selected, setSelected] = useState([]);
  const [editingStage, setEditingStage] = useState(null);
  const [showBulkActions, setShowBulkActions] = useState(false);

  // Filters
  const [search, setSearch] = useState('');
  const [filterStage, setFilterStage] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterCampaign, setFilterCampaign] = useState('');
  const [filterForm, setFilterForm] = useState('');
  const [filterAssigned, setFilterAssigned] = useState('');

  const hdrs = { ...getAuthHeaders(), 'Content-Type': 'application/json' };

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const hdrs = getAuthHeaders();
      const params = new URLSearchParams({ page, limit });
      if (search) params.set('search', search);
      if (filterStage) params.set('lead_stage', filterStage);
      if (filterPriority) params.set('priority', filterPriority);
      if (filterCampaign) params.set('campaign_id', filterCampaign);
      if (filterForm) params.set('form_id', filterForm);
      if (filterAssigned) params.set('assigned_to', filterAssigned);

      const res = await fetch(`${API_URL}/api/meta-ads/leads?${params}`, { headers: hdrs });
      const data = await res.json();
      setLeads(data.leads || []);
      setTotal(data.total || 0);
      setStageCounts(data.stage_counts || {});
    } catch (e) {
      logger.error(e);
    } finally {
      setLoading(false);
    }
  }, [page, search, filterStage, filterPriority, filterCampaign, filterForm, filterAssigned, getAuthHeaders]);

  const fetchMeta = useCallback(async () => {
    try {
      const hdrs = getAuthHeaders();
      const [uRes, cRes, fRes] = await Promise.all([
        fetch(`${API_URL}/api/meta-ads/users`, { headers: hdrs }),
        fetch(`${API_URL}/api/meta-ads/campaigns`, { headers: hdrs }),
        fetch(`${API_URL}/api/meta-ads/forms`, { headers: hdrs }),
      ]);
      setUsers((await uRes.json()).users || []);
      setCampaigns((await cRes.json()).campaigns || []);
      setForms((await fRes.json()).forms || []);
    } catch (e) { logger.error(e); }
  }, [getAuthHeaders]);

  useEffect(() => { fetchMeta(); }, [fetchMeta]);
  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const handleStageChange = async (leadId, newStage) => {
    try {
      await fetch(`${API_URL}/api/meta-ads/leads/${leadId}`, {
        method: 'PATCH', headers: hdrs, body: JSON.stringify({ lead_stage: newStage }),
      });
      setEditingStage(null);
      fetchLeads();
      toast.success('Stage updated');
    } catch (e) { toast.error('Failed to update stage'); }
  };

  const handleAssign = async (leadId, userId) => {
    try {
      await fetch(`${API_URL}/api/meta-ads/leads/${leadId}`, {
        method: 'PATCH', headers: hdrs, body: JSON.stringify({ assigned_to: userId }),
      });
      fetchLeads();
      toast.success('Lead assigned');
    } catch (e) { toast.error('Failed to assign'); }
  };

  const handleBulkUpdate = async (field, value) => {
    if (selected.length === 0) return;
    try {
      await fetch(`${API_URL}/api/meta-ads/leads/bulk-update`, {
        method: 'POST', headers: hdrs,
        body: JSON.stringify({ lead_ids: selected, [field]: value }),
      });
      setSelected([]);
      fetchLeads();
      toast.success(`Updated ${selected.length} leads`);
    } catch (e) { toast.error('Bulk update failed'); }
  };

  const handleDelete = async (leadId) => {
    if (!window.confirm('Delete this lead permanently?')) return;
    try {
      await fetch(`${API_URL}/api/meta-ads/leads/${leadId}`, { method: 'DELETE', headers: hdrs });
      fetchLeads();
      toast.success('Lead deleted');
    } catch (e) { toast.error('Failed to delete'); }
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (filterStage) params.set('lead_stage', filterStage);
      if (filterCampaign) params.set('campaign_id', filterCampaign);
      const res = await fetch(`${API_URL}/api/meta-ads/leads/export?format=csv&${params}`, { headers: hdrs });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'meta_leads_export.csv';
      a.click();
      toast.success('Export downloaded');
    } catch (e) { toast.error('Export failed'); }
  };

  const toggleSelectAll = () => {
    if (selected.length === leads.length) {
      setSelected([]);
    } else {
      setSelected(leads.map(l => l.id));
    }
  };

  const toggleSelect = (id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const clearFilters = () => {
    setSearch(''); setFilterStage(''); setFilterPriority('');
    setFilterCampaign(''); setFilterForm(''); setFilterAssigned('');
    setPage(1);
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-4" data-testid="meta-leads-page">
      {/* Stage pills */}
      <div className="flex flex-wrap gap-2" data-testid="stage-filter-pills">
        <button
          onClick={() => { setFilterStage(''); setPage(1); }}
          className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
            !filterStage ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
        >
          All {stageCounts.all || 0}
        </button>
        {STAGES.map(s => (
          <button key={s} onClick={() => { setFilterStage(s); setPage(1); }}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              filterStage === s ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            {s.replace('_', ' ')} {stageCounts[s] || 0}
          </button>
        ))}
      </div>

      {/* Filter bar */}
      <div className="bg-white rounded-xl border p-4">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
            <input type="text" placeholder="Search name, phone, email..."
              value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
              className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="search-input"
            />
          </div>
          <select value={filterCampaign} onChange={e => { setFilterCampaign(e.target.value); setPage(1); }}
            className="border rounded-lg px-3 py-2 text-sm bg-white" data-testid="filter-campaign">
            <option value="">All Campaigns</option>
            {campaigns.map(c => <option key={c.id} value={c.id}>{c.campaign_name}</option>)}
          </select>
          <select value={filterPriority} onChange={e => { setFilterPriority(e.target.value); setPage(1); }}
            className="border rounded-lg px-3 py-2 text-sm bg-white" data-testid="filter-priority">
            <option value="">All Priorities</option>
            {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          <select value={filterAssigned} onChange={e => { setFilterAssigned(e.target.value); setPage(1); }}
            className="border rounded-lg px-3 py-2 text-sm bg-white" data-testid="filter-assigned">
            <option value="">All Assignees</option>
            <option value="unassigned">Unassigned</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
          {(search || filterStage || filterPriority || filterCampaign || filterForm || filterAssigned) && (
            <Button size="sm" variant="ghost" onClick={clearFilters} className="text-slate-500">
              <XIcon size={14} className="mr-1" /> Clear
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={handleExport} data-testid="export-btn">
            <Download size={14} className="mr-1" /> Export
          </Button>
        </div>
      </div>

      {/* Bulk actions */}
      {selected.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 flex flex-wrap items-center gap-3" data-testid="bulk-actions">
          <span className="text-sm font-medium text-blue-700">{selected.length} selected</span>
          <select onChange={e => { if (e.target.value) handleBulkUpdate('lead_stage', e.target.value); e.target.value = ''; }}
            className="border rounded px-2 py-1 text-sm bg-white" data-testid="bulk-stage-select">
            <option value="">Change Stage...</option>
            {STAGES.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
          </select>
          <select onChange={e => { if (e.target.value) handleBulkUpdate('assigned_to', e.target.value); e.target.value = ''; }}
            className="border rounded px-2 py-1 text-sm bg-white" data-testid="bulk-assign-select">
            <option value="">Assign to...</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
          <select onChange={e => { if (e.target.value) handleBulkUpdate('priority', e.target.value); e.target.value = ''; }}
            className="border rounded px-2 py-1 text-sm bg-white">
            <option value="">Set Priority...</option>
            {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          <Button size="sm" variant="ghost" onClick={() => setSelected([])} className="text-slate-500 ml-auto">
            Cancel
          </Button>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="leads-table">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="p-3 w-10">
                  <input type="checkbox" checked={selected.length === leads.length && leads.length > 0}
                    onChange={toggleSelectAll} className="rounded" />
                </th>
                <th className="p-3 text-left font-medium text-slate-600">Name</th>
                <th className="p-3 text-left font-medium text-slate-600">Phone</th>
                <th className="p-3 text-left font-medium text-slate-600">Email</th>
                <th className="p-3 text-left font-medium text-slate-600">Campaign</th>
                <th className="p-3 text-left font-medium text-slate-600">Stage</th>
                <th className="p-3 text-left font-medium text-slate-600">Priority</th>
                <th className="p-3 text-left font-medium text-slate-600">Assigned</th>
                <th className="p-3 text-left font-medium text-slate-600">Date</th>
                <th className="p-3 text-left font-medium text-slate-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={10} className="p-8 text-center text-slate-400">
                  <RefreshCw className="animate-spin inline mr-2" size={16} /> Loading...
                </td></tr>
              ) : leads.length === 0 ? (
                <tr><td colSpan={10} className="p-8 text-center text-slate-400">No leads found</td></tr>
              ) : leads.map(lead => (
                <tr key={lead.id} className="border-b hover:bg-slate-50 transition-colors" data-testid={`lead-row-${lead.id}`}>
                  <td className="p-3">
                    <input type="checkbox" checked={selected.includes(lead.id)}
                      onChange={() => toggleSelect(lead.id)} className="rounded" />
                  </td>
                  <td className="p-3">
                    <button onClick={() => onViewLead?.(lead.id)} className="font-medium text-slate-900 hover:text-blue-600 text-left">
                      {lead.full_name || '—'}
                    </button>
                    {lead.is_duplicate && (
                      <span className="ml-1.5 text-[10px] bg-orange-100 text-orange-600 px-1.5 py-0.5 rounded">DUP</span>
                    )}
                  </td>
                  <td className="p-3 text-slate-600">{lead.phone || '—'}</td>
                  <td className="p-3 text-slate-600 max-w-[160px] truncate">{lead.email || '—'}</td>
                  <td className="p-3 text-slate-600 max-w-[140px] truncate">{lead.campaign_name || '—'}</td>
                  <td className="p-3">
                    {editingStage === lead.id ? (
                      <select autoFocus value={lead.lead_stage}
                        onChange={e => handleStageChange(lead.id, e.target.value)}
                        onBlur={() => setEditingStage(null)}
                        className="border rounded px-2 py-1 text-xs bg-white">
                        {STAGES.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
                      </select>
                    ) : (
                      <button onClick={() => setEditingStage(lead.id)}
                        className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${STAGE_COLORS[lead.lead_stage] || 'bg-slate-100'}`}>
                        {(lead.lead_stage || 'new').replace('_', ' ')}
                      </button>
                    )}
                  </td>
                  <td className="p-3">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${PRIORITY_BADGES[lead.priority] || ''}`}>
                      {lead.priority}
                    </span>
                  </td>
                  <td className="p-3">
                    <select value={lead.assigned_to || ''} onChange={e => handleAssign(lead.id, e.target.value || null)}
                      className="border rounded px-2 py-1 text-xs bg-white max-w-[120px]">
                      <option value="">Unassigned</option>
                      {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                    </select>
                  </td>
                  <td className="p-3 text-slate-500 text-xs whitespace-nowrap">
                    {lead.created_at ? new Date(lead.created_at).toLocaleDateString() : '—'}
                  </td>
                  <td className="p-3">
                    <div className="flex items-center gap-1">
                      <button onClick={() => onViewLead?.(lead.id)} title="View"
                        className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-blue-600">
                        <Eye size={14} />
                      </button>
                      <button onClick={() => handleDelete(lead.id)} title="Delete"
                        className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {total > limit && (
          <div className="flex items-center justify-between px-4 py-3 border-t bg-slate-50" data-testid="pagination">
            <p className="text-sm text-slate-500">
              Showing {(page - 1) * limit + 1}–{Math.min(page * limit, total)} of {total}
            </p>
            <div className="flex items-center gap-1">
              <Button size="sm" variant="ghost" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
                <ChevronLeft size={16} />
              </Button>
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                const p = page <= 3 ? i + 1 : page - 2 + i;
                if (p > totalPages) return null;
                return (
                  <Button key={p} size="sm" variant={p === page ? 'default' : 'ghost'}
                    onClick={() => setPage(p)} className={p === page ? 'bg-slate-900 text-white' : ''}>
                    {p}
                  </Button>
                );
              })}
              <Button size="sm" variant="ghost" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>
                <ChevronRight size={16} />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
